/**
 * OneSignal Web SDK v16 — unified push channel for browsers + PWA + Android WebView.
 *
 * WHAT THIS DOES
 * ──────────────
 * Initializes the OneSignal Web SDK after React boots, then associates
 * the device's OneSignal subscription with the current app user via
 * `OneSignal.login(userId)`. This sets `external_user_id` on the
 * OneSignal subscription so the backend can target this user with
 * `include_external_user_ids: [userId]` and the push reaches every
 * device they've granted permission on.
 *
 * WHEN IT RUNS
 * ────────────
 *   1. App boot — load SDK, initialize.
 *   2. After auth — call `OneSignal.login(userId)`.
 *   3. After explicit user opt-in (Settings toggle / first push prompt)
 *      — call `OneSignal.Notifications.requestPermission()` to actually
 *      ask the OS for notification permission. We never auto-prompt;
 *      that's the OneSignal best-practice and avoids permission
 *      blacklisting.
 *
 * PLATFORM COVERAGE
 * ─────────────────
 *   • Desktop Chrome / Edge / Firefox          ✅ via Web Push (FCM/Mozilla)
 *   • Android Chrome (browser + PWA install)   ✅ via FCM
 *   • iOS Safari PWA (Home Screen, 16.4+)      ✅ via APNs (limited)
 *   • Android Studio WebView wrapper           ✅ via FCM (if WebView
 *                                                 has Google Play Services
 *                                                 and our SW registers)
 *   • iOS Despia / WKWebView                   ❌ not supported — falls
 *                                                 back to native OneSignal
 *                                                 SDK (already integrated
 *                                                 in the iOS wrapper)
 *
 * SAFE COEXISTENCE WITH VAPID
 * ───────────────────────────
 * VAPID push subscriptions remain in /api/notifications/subscribe and
 * are still delivered by the backend via pywebpush. Both can coexist
 * on the same device — typically OneSignal becomes the primary channel
 * once the user opts in via OneSignal, and VAPID stays as a backup for
 * users who never went through the OneSignal flow. The backend's
 * dual-delivery in notification_service.py covers both.
 */

import axios from 'axios';

const LOG = '[OneSignalWeb]';

// Module-level state
let _initStarted = false;
let _initPromise = null;
let _appIdCache = null;
let _loggedInUserId = null;

/**
 * Fetch the OneSignal App ID from the backend. Cached per session.
 * Returns null if OneSignal isn't configured on the server side or if
 * the App ID isn't a valid UUID (safeguard against preview-env stubs
 * like `scripture-token-rca` that would crash the SDK).
 */
async function fetchAppId() {
  if (_appIdCache !== null) return _appIdCache;
  try {
    const res = await axios.get(
      `${window.location.origin}/api/notifications/onesignal-app-id`,
      { timeout: 4000 }
    );
    const id = res.data?.app_id;
    // OneSignal App IDs are UUIDs (8-4-4-4-12 hex chars).
    const isValidUuid =
      typeof id === 'string' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
    _appIdCache = isValidUuid ? id : '';
    if (!isValidUuid && id) {
      console.warn(`${LOG} App ID "${id}" is not a valid UUID — skipping OneSignal init.`);
    }
    return _appIdCache;
  } catch (e) {
    console.warn(`${LOG} Failed to fetch OneSignal App ID:`, e?.message);
    _appIdCache = '';
    return '';
  }
}

/**
 * Initialize the OneSignal Web SDK. Idempotent — safe to call repeatedly.
 *
 * Returns a Promise that resolves to:
 *   • true  — SDK initialized successfully
 *   • false — SDK not available, or App ID missing, or environment
 *             doesn't support web push (e.g. iOS Despia WKWebView)
 *
 * Notes:
 *   - We use the OneSignalDeferred queue (set up in index.html) so the
 *     init call is buffered until the SDK script finishes loading.
 *   - We DO NOT auto-prompt for permission. The user must explicitly
 *     request it via `requestPushPermission()` below.
 */
export async function initOneSignalWeb() {
  if (_initPromise) return _initPromise;
  if (_initStarted) return false;
  _initStarted = true;

  // iOS Despia / standalone WKWebView — Safari's WKWebView in third-party
  // wrappers does not support Web Push. Skip Web SDK init entirely and
  // let the existing native Despia bridge (`setonesignalplayerid://`)
  // handle push registration on iOS.
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('despia')) {
    console.log(`${LOG} Skipping init — Despia native iOS uses native OneSignal SDK.`);
    return false;
  }

  _initPromise = (async () => {
    const appId = await fetchAppId();
    if (!appId) {
      console.warn(`${LOG} No valid App ID — Web SDK will not init.`);
      return false;
    }

    if (!Array.isArray(window.OneSignalDeferred)) {
      // Defensive — should always be set up by index.html, but if SDK
      // script failed to load, give up gracefully.
      console.warn(`${LOG} OneSignalDeferred queue missing — SDK script likely failed to load.`);
      return false;
    }

    return new Promise((resolve) => {
      window.OneSignalDeferred.push(async (OneSignal) => {
        try {
          await OneSignal.init({
            appId,
            // Keep our own SW; OneSignal SDK Worker is imported into it
            // via `importScripts` in /sw.js.
            serviceWorkerParam: { scope: '/' },
            serviceWorkerPath: '/sw.js',
            // Don't auto-prompt — we control timing in the UI.
            promptOptions: {
              slidedown: { enabled: false, autoPrompt: false },
            },
            // OneSignal's Welcome Notification ("Thanks for subscribing!")
            // is noisy and not aligned with our brand voice. Disable.
            welcomeNotification: { disable: true },
            // Allow over localhost during dev — production uses https.
            allowLocalhostAsSecureOrigin: window.location.hostname === 'localhost',
            // Reduce console noise.
            notifyButton: { enable: false },
          });
          console.log(`${LOG} Init complete (appId=${appId.slice(0, 8)}…).`);

          // Listen for subscription state changes — log them for diag.
          try {
            OneSignal.User.PushSubscription.addEventListener?.('change', (ev) => {
              const id = ev?.current?.id || OneSignal.User.PushSubscription.id;
              const optedIn = ev?.current?.optedIn ?? OneSignal.User.PushSubscription.optedIn;
              console.log(`${LOG} Subscription change — id=${id ? id.slice(0, 8) + '…' : 'none'} optedIn=${optedIn}`);
              try {
                window.__eili_onesignal_diag__ = {
                  ...(window.__eili_onesignal_diag__ || {}),
                  subscriptionId: id || null,
                  optedIn: !!optedIn,
                  changeAt: new Date().toISOString(),
                };
              } catch { /* ignore */ }
            });
          } catch { /* SDK version may not support addEventListener — non-fatal */ }

          // Surface init state on a global for diagnostics.
          try {
            window.__eili_onesignal_diag__ = {
              ...(window.__eili_onesignal_diag__ || {}),
              initialized: true,
              appIdPrefix: appId.slice(0, 8),
              initAt: new Date().toISOString(),
              optedIn: !!OneSignal.User?.PushSubscription?.optedIn,
              subscriptionId: OneSignal.User?.PushSubscription?.id || null,
            };
          } catch { /* ignore */ }

          resolve(true);
        } catch (err) {
          console.error(`${LOG} Init failed:`, err);
          try {
            window.__eili_onesignal_diag__ = {
              ...(window.__eili_onesignal_diag__ || {}),
              initialized: false,
              initError: String(err?.message || err),
              initAt: new Date().toISOString(),
            };
          } catch { /* ignore */ }
          resolve(false);
        }
      });
    });
  })();

  return _initPromise;
}

/**
 * Associate the OneSignal device with our app's user ID so backend
 * pushes via `include_external_user_ids: [userId]` reach this device.
 * Idempotent within a session — won't re-call if same userId.
 *
 * Call this AFTER `initOneSignalWeb()` resolves. Safe to call before
 * init too — it'll wait via the OneSignalDeferred queue.
 */
export async function loginOneSignalWeb(userId) {
  if (!userId) return false;
  if (_loggedInUserId === userId) return true;

  // Best-effort: ensure init has been kicked off.
  initOneSignalWeb().catch(() => {});

  if (!Array.isArray(window.OneSignalDeferred)) return false;

  return new Promise((resolve) => {
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        await OneSignal.login(String(userId));
        _loggedInUserId = userId;
        console.log(`${LOG} Logged in as user ${userId}.`);
        try {
          window.__eili_onesignal_diag__ = {
            ...(window.__eili_onesignal_diag__ || {}),
            loggedInUserId: userId,
            loggedInAt: new Date().toISOString(),
            subscriptionIdAfterLogin: OneSignal.User?.PushSubscription?.id || null,
          };
        } catch { /* ignore */ }

        // Belt-and-suspenders: also POST the subscription_id to our
        // backend so it stores the link locally for fallback targeting.
        // Wait one tick for OneSignal to settle the subscription.
        setTimeout(async () => {
          try {
            const subId = OneSignal.User?.PushSubscription?.id;
            if (subId) {
              await axios.post(
                `${window.location.origin}/api/notifications/link-onesignal`,
                { subscription_id: subId },
                { withCredentials: true, timeout: 6000 },
              );
              console.log(`${LOG} Backend link OK for sub ${subId.slice(0, 8)}…`);
            }
          } catch (e) {
            console.warn(`${LOG} Backend link failed:`, e?.response?.status || e?.message);
          }
        }, 800);

        resolve(true);
      } catch (err) {
        console.error(`${LOG} login() failed:`, err);
        resolve(false);
      }
    });
  });
}

/**
 * Disassociate the OneSignal device from the user (call on app logout).
 * The device subscription remains; only the external_user_id is cleared.
 */
export async function logoutOneSignalWeb() {
  _loggedInUserId = null;
  if (!Array.isArray(window.OneSignalDeferred)) return false;
  return new Promise((resolve) => {
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        await OneSignal.logout();
        console.log(`${LOG} Logged out.`);
        resolve(true);
      } catch {
        resolve(false);
      }
    });
  });
}

/**
 * Explicitly ask the OS for notification permission AND opt the user
 * into push. Call this from a user-initiated flow (Settings toggle,
 * "Enable notifications" button) — never silently on app load. Browsers
 * blacklist sites that auto-prompt without context.
 *
 * Returns 'granted' | 'denied' | 'default' | 'unsupported'.
 */
export async function requestOneSignalPermission() {
  if (!Array.isArray(window.OneSignalDeferred)) return 'unsupported';

  // First make sure init has run.
  await initOneSignalWeb();

  return new Promise((resolve) => {
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        // v16 API: requestPermission triggers the OS dialog AND opts
        // the user in to push if granted.
        const granted = await OneSignal.Notifications.requestPermission();
        const permission = OneSignal.Notifications.permission ? 'granted' : (granted ? 'granted' : 'denied');
        console.log(`${LOG} Permission result: ${permission}`);
        resolve(permission);
      } catch (err) {
        console.warn(`${LOG} requestPermission failed:`, err);
        // Fall back to the standard browser API.
        try {
          const r = await Notification.requestPermission();
          resolve(r);
        } catch {
          resolve('default');
        }
      }
    });
  });
}

/**
 * Read the current OneSignal subscription state without triggering a
 * permission prompt. Useful for the Settings UI to reflect the user's
 * current push status.
 *
 * Returns: { available, optedIn, subscriptionId, permission }
 */
export async function getOneSignalState() {
  const out = {
    available: false,
    optedIn: false,
    subscriptionId: null,
    permission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
  };
  if (!Array.isArray(window.OneSignalDeferred)) return out;

  return new Promise((resolve) => {
    let resolved = false;
    const fallback = setTimeout(() => {
      if (!resolved) { resolved = true; resolve(out); }
    }, 1500);
    window.OneSignalDeferred.push(async (OneSignal) => {
      if (resolved) return;
      try {
        out.available = true;
        out.optedIn = !!OneSignal.User?.PushSubscription?.optedIn;
        out.subscriptionId = OneSignal.User?.PushSubscription?.id || null;
        out.permission = OneSignal.Notifications?.permission ? 'granted' : (Notification?.permission || 'default');
      } catch { /* ignore */ }
      clearTimeout(fallback);
      resolved = true;
      resolve(out);
    });
  });
}
